import React, { useState, useEffect, useMemo } from 'react';
import { adminAPI, partialAPI, mockAPI } from '@/api/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { User, Mail, Phone, Coins, Edit, Save, X, Calendar, Clock, MapPin, History, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/api/client';

// Helper function to check if a date is in the past
const isDatePast = (dateString) => {
  if (!dateString) return false;
  const examDate = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return examDate < today;
};

const StudentProfile = ({ student, onClose, onUpdate, userRole }) => {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState({ partial: [], mock: [] });
  const [editingCredits, setEditingCredits] = useState(false);
  const [canEditCredits, setCanEditCredits] = useState(false);
  
  const [formData, setFormData] = useState({
    name: student.name || '',
    email: student.email || '',
    phone: student.phone || '',
    batch_code: student.batch_code || '',
    exam_category: student.exam_category || 'PB',
    password: '',
  });

  const [moduleCredits, setModuleCredits] = useState({
    listening: student.module_credits?.listening || 0,
    reading: student.module_credits?.reading || 0,
    writing: student.module_credits?.writing || 0,
    speaking: student.module_credits?.speaking || 0,
    mock: student.module_credits?.mock || 0,
  });

  // Credit Reason Modal State
  const [showCreditReasonModal, setShowCreditReasonModal] = useState(false);
  const [creditChangeReason, setCreditChangeReason] = useState('');
  const [pendingCredits, setPendingCredits] = useState(null);
  const [creditChangeSummary, setCreditChangeSummary] = useState([]);

  // Separate bookings into recent (upcoming) and past
  const { recentPartialBookings, pastPartialBookings } = useMemo(() => {
    const recent = [];
    const past = [];
    
    bookings.partial.forEach(booking => {
      const examDate = booking.exam?.exam_date;
      if (isDatePast(examDate)) {
        past.push(booking);
      } else {
        recent.push(booking);
      }
    });
    
    return { recentPartialBookings: recent, pastPartialBookings: past };
  }, [bookings.partial]);

  const { recentMockBookings, pastMockBookings } = useMemo(() => {
    const recent = [];
    const past = [];
    
    bookings.mock.forEach(booking => {
      // Use booking.exam.lrw_date (not mock_exam) as backend sends 'exam' key
      const examDate = booking.exam?.lrw_date;
      if (isDatePast(examDate)) {
        past.push(booking);
      } else {
        recent.push(booking);
      }
    });
    
    return { recentMockBookings: recent, pastMockBookings: past };
  }, [bookings.mock]);

  useEffect(() => {
    fetchBookings();
    fetchStudentCredits();
    checkCreditEditPermission();
    
    // Listen for booking events from anywhere in the app
    const handleBookingChange = () => {
      fetchBookings();
      fetchStudentCredits();
    };
    
    window.addEventListener('bookingCreated', handleBookingChange);
    window.addEventListener('bookingCancelled', handleBookingChange);
    
    return () => {
      window.removeEventListener('bookingCreated', handleBookingChange);
      window.removeEventListener('bookingCancelled', handleBookingChange);
    };
  }, [student.id]);

  const checkCreditEditPermission = async () => {
    // Admin always has permission
    if (userRole === 'admin') {
      setCanEditCredits(true);
      return;
    }
    
    // For moderators, check their permission
    if (userRole === 'moderator') {
      try {
        const response = await apiClient.get('/auth/me');
        setCanEditCredits(response.data.can_edit_student_credits || false);
      } catch (error) {
        console.error('Failed to check permissions:', error);
        setCanEditCredits(false);
      }
    }
  };

  const fetchStudentCredits = async () => {
    try {
      const response = await apiClient.get(`/admin/students/${student.id}`);
      if (response.data.module_credits) {
        setModuleCredits({
          listening: response.data.module_credits.listening || 0,
          reading: response.data.module_credits.reading || 0,
          writing: response.data.module_credits.writing || 0,
          speaking: response.data.module_credits.speaking || 0,
          mock: response.data.module_credits.mock || 0,
        });
      }
    } catch (error) {
      console.error('Failed to fetch student credits:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      // Fetch student's bookings (apiClient already has /api prefix)
      const response = await apiClient.get(`/admin/students/${student.id}/bookings`);
      
      setBookings({
        partial: response.data.partial_bookings || [],
        mock: response.data.mock_bookings || []
      });
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  };

  const handleCancelBooking = async (bookingId, bookingType) => {
    if (!window.confirm('Are you sure you want to cancel this booking? The student will receive an email notification.')) {
      return;
    }

    const reason = prompt('Enter cancellation reason:', 'Cancelled by institute based on student request');
    if (!reason) return;

    try {
      await apiClient.post(`/admin/students/${student.id}/bookings/${bookingId}/cancel`, {
        type: bookingType,
        reason: reason
      });
      
      toast.success('Booking cancelled successfully. Student has been notified via email.');
      await fetchBookings();
      onUpdate();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to cancel booking';
      toast.error(errorMsg);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Update student
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        batch_code: formData.batch_code,
        exam_category: formData.exam_category,
      };
      
      // Only include password if it's provided
      if (formData.password) {
        updateData.password = formData.password;
      }
      
      await adminAPI.updateStudent(student.id, updateData);
      
      toast.success('Student updated successfully!');
      setEditing(false);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update student');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveModuleCredits = async () => {
    // Calculate changes
    const changes = [];
    const oldCredits = {
      listening: student.module_credits?.listening || 0,
      reading: student.module_credits?.reading || 0,
      writing: student.module_credits?.writing || 0,
      speaking: student.module_credits?.speaking || 0,
      mock: student.module_credits?.mock || 0,
    };
    
    const newCredits = {
      listening: parseInt(moduleCredits.listening) || 0,
      reading: parseInt(moduleCredits.reading) || 0,
      writing: parseInt(moduleCredits.writing) || 0,
      speaking: parseInt(moduleCredits.speaking) || 0,
      mock: parseInt(moduleCredits.mock) || 0,
    };
    
    // Find what changed
    ['listening', 'reading', 'writing', 'speaking', 'mock'].forEach(module => {
      const diff = newCredits[module] - oldCredits[module];
      if (diff !== 0) {
        changes.push({
          module: module.charAt(0).toUpperCase() + module.slice(1),
          old: oldCredits[module],
          new: newCredits[module],
          diff: diff > 0 ? `+${diff}` : `${diff}`
        });
      }
    });
    
    if (changes.length === 0) {
      toast.info('কোন পরিবর্তন হয়নি');
      setEditingCredits(false);
      return;
    }
    
    // Store pending changes and show reason modal
    setPendingCredits(newCredits);
    setCreditChangeSummary(changes);
    setShowCreditReasonModal(true);
  };
  
  const handleConfirmCreditChange = async () => {
    if (!creditChangeReason.trim()) {
      toast.error('অনুগ্রহ করে কারণ লিখুন');
      return;
    }
    
    setLoading(true);
    try {
      await adminAPI.updateStudentModuleCredits(student.id, {
        listening_credits: pendingCredits.listening,
        reading_credits: pendingCredits.reading,
        writing_credits: pendingCredits.writing,
        speaking_credits: pendingCredits.speaking,
        mock_credits: pendingCredits.mock,
        reason: creditChangeReason.trim()
      });
      
      toast.success('Module credits updated successfully!');
      setEditingCredits(false);
      setShowCreditReasonModal(false);
      setCreditChangeReason('');
      setPendingCredits(null);
      setCreditChangeSummary([]);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update credits');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-6 h-6 text-blue-600" />
            Student Profile
          </DialogTitle>
          <DialogDescription>
            View and manage student information and bookings
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Student Info Card */}
          <Card className="bg-gradient-to-r from-blue-50 to-purple-50">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg">Student Information</CardTitle>
                {!editing ? (
                  <Button size="sm" onClick={() => setEditing(true)} variant="outline">
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSave} disabled={loading}>
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                    <Button size="sm" onClick={() => setEditing(false)} variant="outline">
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {editing ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Student name"
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="student@example.com"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="01XXXXXXXXX"
                      />
                    </div>
                    <div>
                      <Label>Batch Code</Label>
                      <Input
                        value={formData.batch_code}
                        onChange={(e) => setFormData({ ...formData, batch_code: e.target.value })}
                        placeholder="RICU130"
                      />
                    </div>
                    <div>
                      <Label>Exam Category</Label>
                      <Select 
                        value={formData.exam_category} 
                        onValueChange={(val) => setFormData({ ...formData, exam_category: val })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PB">Paper Based (PB)</SelectItem>
                          <SelectItem value="CD">Computer Delivered (CD)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label>New Password (leave empty to keep current)</Label>
                      <Input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Enter new password"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <div>
                      <div className="text-sm text-gray-600">Name</div>
                      <div className="font-semibold">
                        {student.name}
                        {student.candidate_no && (
                          <span className="ml-2 text-sm text-blue-600">#{student.candidate_no}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-500" />
                    <div>
                      <div className="text-sm text-gray-600">Email</div>
                      <div className="font-semibold">{student.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-500" />
                    <div>
                      <div className="text-sm text-gray-600">Phone</div>
                      <div className="font-semibold">{student.phone || 'N/A'}</div>
                    </div>
                  </div>
                  {student.batch_code && (
                    <div>
                      <div className="text-sm text-gray-600">Batch</div>
                      <Badge className="mt-1">{student.batch_code}</Badge>
                    </div>
                  )}
                  {student.exam_category && (
                    <div>
                      <div className="text-sm text-gray-600">Exam Category</div>
                      <Badge 
                        variant={student.exam_category === 'PB' ? 'default' : 'secondary'} 
                        className="mt-1"
                      >
                        {student.exam_category === 'PB' ? 'Paper Based (PB)' : 'Computer Delivered (CD)'}
                      </Badge>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Module Credits Management */}
          <Card className="bg-gradient-to-r from-blue-50 to-purple-50">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Coins className="w-5 h-5 text-blue-600" />
                    Module Credits
                  </CardTitle>
                  <CardDescription>Manage individual module credits</CardDescription>
                </div>
                {canEditCredits && (
                  !editingCredits ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingCredits(true)}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Credits
                    </Button>
                  ) : (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingCredits(false);
                        setModuleCredits({
                          listening: student.module_credits?.listening || 0,
                          reading: student.module_credits?.reading || 0,
                          writing: student.module_credits?.writing || 0,
                          speaking: student.module_credits?.speaking || 0,
                          mock: student.module_credits?.mock || 0,
                        });
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveModuleCredits}
                      disabled={loading}
                      className="bg-gradient-to-r from-blue-600 to-purple-600"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </div>
                  )
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="listening_credits" className="text-blue-700 font-semibold">Listening</Label>
                  {editingCredits ? (
                    <Input
                      id="listening_credits"
                      type="number"
                      min="0"
                      value={moduleCredits.listening}
                      onChange={(e) => setModuleCredits({...moduleCredits, listening: e.target.value})}
                      className="border-blue-300"
                    />
                  ) : (
                    <div className="text-2xl font-bold text-blue-600">{moduleCredits.listening}</div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reading_credits" className="text-green-700 font-semibold">Reading</Label>
                  {editingCredits ? (
                    <Input
                      id="reading_credits"
                      type="number"
                      min="0"
                      value={moduleCredits.reading}
                      onChange={(e) => setModuleCredits({...moduleCredits, reading: e.target.value})}
                      className="border-green-300"
                    />
                  ) : (
                    <div className="text-2xl font-bold text-green-600">{moduleCredits.reading}</div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="writing_credits" className="text-purple-700 font-semibold">Writing</Label>
                  {editingCredits ? (
                    <Input
                      id="writing_credits"
                      type="number"
                      min="0"
                      value={moduleCredits.writing}
                      onChange={(e) => setModuleCredits({...moduleCredits, writing: e.target.value})}
                      className="border-purple-300"
                    />
                  ) : (
                    <div className="text-2xl font-bold text-purple-600">{moduleCredits.writing}</div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="speaking_credits" className="text-orange-700 font-semibold">Speaking</Label>
                  {editingCredits ? (
                    <Input
                      id="speaking_credits"
                      type="number"
                      min="0"
                      value={moduleCredits.speaking}
                      onChange={(e) => setModuleCredits({...moduleCredits, speaking: e.target.value})}
                      className="border-orange-300"
                    />
                  ) : (
                    <div className="text-2xl font-bold text-orange-600">{moduleCredits.speaking}</div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mock_credits" className="text-pink-700 font-semibold">Mock Test</Label>
                  {editingCredits ? (
                    <Input
                      id="mock_credits"
                      type="number"
                      min="0"
                      value={moduleCredits.mock}
                      onChange={(e) => setModuleCredits({...moduleCredits, mock: e.target.value})}
                      className="border-pink-300"
                    />
                  ) : (
                    <div className="text-2xl font-bold text-pink-600">{moduleCredits.mock}</div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Partial Bookings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Partial Exam Bookings</CardTitle>
              <CardDescription>History of partial exam bookings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {bookings.partial.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No partial bookings yet</div>
              ) : (
                <>
                  {/* Recent/Upcoming Bookings */}
                  {recentPartialBookings.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Recent Bookings ({recentPartialBookings.length})
                      </h4>
                      <div className="space-y-3">
                        {recentPartialBookings.map((booking) => (
                          <div key={booking.id} className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex justify-between items-start">
                              <div className="space-y-2 flex-1">
                                <div className="font-semibold text-blue-600">{booking.exam?.module}</div>
                                <div className="flex items-center gap-2 text-sm">
                                  <Calendar className="w-4 h-4" />
                                  {booking.exam?.exam_date}
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                  <Clock className="w-4 h-4" />
                                  {booking.exam?.start_time} - {booking.exam?.end_time}
                                </div>
                                {booking.exam?.room && (
                                  <div className="flex items-center gap-2 text-sm">
                                    <MapPin className="w-4 h-4" />
                                    Room {booking.exam?.room}
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-2">
                                <Badge variant={
                                  booking.status === 'BOOKED' ? 'default' : 
                                  booking.status === 'CANCELLED' ? 'destructive' : 
                                  'secondary'
                                }>
                                  {booking.status}
                                </Badge>
                                {booking.status === 'BOOKED' && (
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => handleCancelBooking(booking.id, 'partial')}
                                  >
                                    <X className="w-4 h-4 mr-1" />
                                    Cancel
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Past Bookings */}
                  {pastPartialBookings.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-500 mb-3 flex items-center gap-2">
                        <History className="w-4 h-4" />
                        Past Bookings ({pastPartialBookings.length})
                      </h4>
                      <div className="space-y-3">
                        {pastPartialBookings.map((booking) => (
                          <div key={booking.id} className="p-4 bg-gray-100 rounded-lg opacity-75">
                            <div className="flex justify-between items-start">
                              <div className="space-y-2 flex-1">
                                <div className="font-semibold text-gray-600">{booking.exam?.module}</div>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                  <Calendar className="w-4 h-4" />
                                  {booking.exam?.exam_date}
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                  <Clock className="w-4 h-4" />
                                  {booking.exam?.start_time} - {booking.exam?.end_time}
                                </div>
                                {booking.exam?.room && (
                                  <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <MapPin className="w-4 h-4" />
                                    Room {booking.exam?.room}
                                  </div>
                                )}
                              </div>
                              <Badge variant={
                                booking.status === 'BOOKED' ? 'secondary' : 
                                booking.status === 'CANCELLED' ? 'destructive' : 
                                'secondary'
                              }>
                                {booking.status === 'BOOKED' ? 'COMPLETED' : booking.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Mock Bookings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Mock Test Bookings</CardTitle>
              <CardDescription>History of mock test bookings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {bookings.mock.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No mock bookings yet</div>
              ) : (
                <>
                  {/* Recent/Upcoming Mock Bookings */}
                  {recentMockBookings.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Recent Bookings ({recentMockBookings.length})
                      </h4>
                      <div className="space-y-4">
                        {recentMockBookings.map((booking) => (
                          <div key={booking.id} className="p-4 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg">
                            <div className="flex justify-between items-start mb-3">
                              <div className="font-bold text-blue-600 text-lg">
                                {booking.exam?.mock_uid}
                              </div>
                              {booking.lrw_status === 'BOOKED' && (
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleCancelBooking(booking.id, 'mock')}
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  Cancel Mock
                                </Button>
                              )}
                            </div>
                            <div className="space-y-3">
                              {/* LRW */}
                              <div className="border-t pt-2">
                                <div className="font-semibold mb-2">LRW</div>
                                <div className="flex items-center gap-2 text-sm">
                                  <Calendar className="w-4 h-4" />
                                  {booking.exam?.lrw_date}
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                  <Clock className="w-4 h-4" />
                                  {booking.exam?.lrw_start} - {booking.exam?.lrw_end}
                                </div>
                                <Badge variant={
                                  booking.lrw_status === 'BOOKED' ? 'default' : 'destructive'
                                } className="mt-2">
                                  {booking.lrw_status}
                                </Badge>
                              </div>

                              {/* Speaking */}
                              {booking.speaking_slot && (
                                <div className="border-t pt-2">
                                  <div className="font-semibold mb-2">Speaking</div>
                                  <div className="flex items-center gap-2 text-sm">
                                    <Calendar className="w-4 h-4" />
                                    {booking.speaking_slot?.speak_date}
                                  </div>
                                  <div className="flex items-center gap-2 text-sm">
                                    <Clock className="w-4 h-4" />
                                    {booking.speaking_slot?.start_time} - {booking.speaking_slot?.end_time}
                                  </div>
                                  {booking.speaking_slot?.room && (
                                    <div className="flex items-center gap-2 text-sm">
                                      <MapPin className="w-4 h-4" />
                                      Room {booking.speaking_slot?.room}
                                    </div>
                                  )}
                                  <Badge variant={
                                    booking.speak_status === 'BOOKED' ? 'default' : 'destructive'
                                  } className="mt-2">
                                    {booking.speak_status}
                                  </Badge>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Past Mock Bookings */}
                  {pastMockBookings.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-500 mb-3 flex items-center gap-2">
                        <History className="w-4 h-4" />
                        Past Bookings ({pastMockBookings.length})
                      </h4>
                      <div className="space-y-4">
                        {pastMockBookings.map((booking) => (
                          <div key={booking.id} className="p-4 bg-gray-100 rounded-lg opacity-75">
                            <div className="flex justify-between items-start mb-3">
                              <div className="font-bold text-gray-600 text-lg">
                                {booking.exam?.mock_uid}
                              </div>
                              <Badge variant="secondary">COMPLETED</Badge>
                            </div>
                            <div className="space-y-3">
                              {/* LRW */}
                              <div className="border-t pt-2">
                                <div className="font-semibold mb-2 text-gray-600">LRW</div>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                  <Calendar className="w-4 h-4" />
                                  {booking.exam?.lrw_date}
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                  <Clock className="w-4 h-4" />
                                  {booking.exam?.lrw_start} - {booking.exam?.lrw_end}
                                </div>
                              </div>

                              {/* Speaking */}
                              {booking.speaking_slot && (
                                <div className="border-t pt-2">
                                  <div className="font-semibold mb-2 text-gray-600">Speaking</div>
                                  <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <Calendar className="w-4 h-4" />
                                    {booking.speaking_slot?.speak_date}
                                  </div>
                                  <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <Clock className="w-4 h-4" />
                                    {booking.speaking_slot?.start_time} - {booking.speaking_slot?.end_time}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
      
      {/* Credit Change Reason Modal */}
      <Dialog open={showCreditReasonModal} onOpenChange={(open) => {
        if (!open) {
          setShowCreditReasonModal(false);
          setCreditChangeReason('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" />
              Credit পরিবর্তনের কারণ
            </DialogTitle>
            <DialogDescription>
              Credit পরিবর্তন করার আগে কারণ উল্লেখ করুন। এটি Activity Log এ সংরক্ষিত হবে।
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Change Summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm font-semibold text-blue-800 mb-2">পরিবর্তনের সারাংশ:</p>
              <div className="space-y-1">
                {creditChangeSummary.map((change, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-gray-700">{change.module}:</span>
                    <span className={change.diff.startsWith('+') ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                      {change.old} → {change.new} ({change.diff})
                    </span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Reason Input */}
            <div className="space-y-2">
              <Label htmlFor="credit_reason" className="text-gray-700 font-semibold">
                কারণ <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="credit_reason"
                placeholder="উদাহরণ: Student এর অনুরোধে Extra credit যোগ করা হলো / ভুল booking এর জন্য credit ফেরত দেওয়া হলো..."
                value={creditChangeReason}
                onChange={(e) => setCreditChangeReason(e.target.value)}
                className="min-h-[100px] border-amber-300 focus:border-amber-500"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowCreditReasonModal(false);
                setCreditChangeReason('');
              }}
            >
              বাতিল
            </Button>
            <Button
              onClick={handleConfirmCreditChange}
              disabled={loading || !creditChangeReason.trim()}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            >
              {loading ? '⏳ Saving...' : '✓ নিশ্চিত করুন'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default StudentProfile;
